import type { ColumnState, FilterModel, GridApi } from "ag-grid-enterprise";
import type { ForecastTableControlValues } from "../components/tableControls/forecastTableControlValues";
import { partitionViewOwnedFilters, setFilterValues } from "./resolveViewRows";
import {
  SAVED_VIEW_VERSION,
  type SavedViewPayload,
  type ViewRows,
  type ViewSections
} from "./savedViewPayload";

/**
 * The only ``ColumnState`` fields a saved view carries: the ones a user
 * chooses by dragging, resizing, pinning or hiding. Everything else in the
 * state AG Grid reports is code-owned (aggFunc, headerName, sortType) and
 * persisting it would pin a colDef default against later releases —
 * ``width`` and ``flex`` travel as a pair because the pair is the signal
 * (flexing vs dragged). Sorting is the row selection's ``sortBy``, so no
 * sort field belongs here.
 */
export const PERSISTED_COLUMN_STATE_FIELDS = [
  "colId",
  "hide",
  "width",
  "flex",
  "pinned"
] as const;

/** Current state to snapshot. */
export interface ViewCaptureSource {
  tableControls: ForecastTableControlValues;
  rows: ViewRows;
  /**
   * The metric names ``rows`` resolves to on this grid, or null for every
   * metric — what the grid's metric filter is compared against.
   */
  resolvedMetrics: string[] | null;
  /** The live grid, for the layout section and the metric filter. Null before the grid mounts. */
  api: GridApi | null;
}

/** Keep only the user-chosen fields of each column's state. */
export function captureColumnState(
  api: GridApi,
  fields: readonly (keyof ColumnState)[] = PERSISTED_COLUMN_STATE_FIELDS
): ColumnState[] {
  return api.getColumnState().map((column) => {
    const kept: ColumnState = { colId: column.colId };
    for (const field of fields) {
      const value = column[field];
      if (value !== undefined) Object.assign(kept, { [field]: value });
    }
    return kept;
  });
}

/**
 * Drop the columns the row selection owns from a filter model.
 *
 * Applied on capture *and* on replay (see ``resolveViewRows``) so a view
 * saved by an older build can't reintroduce a metric filter that fights the
 * selection it also carries.
 */
export function stripViewOwnedFilters(
  model: FilterModel | null
): FilterModel | null {
  const { planner } = partitionViewOwnedFilters(model);
  return Object.keys(planner).length ? planner : null;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

/**
 * The row selection to save: ``rows`` as applied, unless the planner has
 * changed the grid's metric filter since — then their list, by hand.
 *
 * The metric column's own filter is the one way to shape the rows beyond
 * the presets, so a save has to honour it; but a filter still equal to what
 * the preset resolved to keeps the preset, which tracks the release that
 * owns it rather than freezing today's list. AG Grid reports a set filter
 * with every value ticked as no model at all, so no metric filter under a
 * narrowing selection means the planner widened it back to every metric.
 */
export function captureRows(
  rows: ViewRows,
  resolvedMetrics: string[] | null,
  api: GridApi | null
): ViewRows {
  if (!api) return rows;
  const filtered = setFilterValues(api.getFilterModel().metric_name);
  if (!filtered) {
    return resolvedMetrics
      ? { ...rows, preset: null, categories: [], metrics: [] }
      : rows;
  }
  if (resolvedMetrics && sameSet(filtered, resolvedMetrics)) return rows;
  return { ...rows, preset: null, categories: [], metrics: filtered };
}

/** Snapshot the toggled sections of the current state. */
export function captureViewPayload(
  sections: ViewSections,
  source: ViewCaptureSource
): SavedViewPayload {
  const { tableControls, rows, resolvedMetrics, api } = source;
  const payload: SavedViewPayload = { version: SAVED_VIEW_VERSION };

  if (sections.dataShape) {
    payload.dataShape = {
      calendarView: tableControls.calendarView,
      timePeriod: tableControls.timePeriod,
      planBy: tableControls.planBy,
      planningYear: tableControls.planningYear
    };
  }
  if (sections.rows) payload.rows = captureRows(rows, resolvedMetrics, api);
  if (sections.layout) {
    payload.layout = {
      weekLabels: tableControls.weekLabels,
      gridStyleGrouping: tableControls.gridStyleGrouping
    };
    if (api) {
      payload.layout.columnState = captureColumnState(api);
      payload.layout.filterModel =
        stripViewOwnedFilters(api.getFilterModel()) ?? undefined;
    }
  }
  return payload;
}
